var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getConfig } from "@jti/fe-common";
import { getUser } from "../../utils";
import { CasBaseApi } from "./CasBaseApi";
import { Endpoints, EvoEndpoints } from "./CasEndpoints";
export class CasAddressesApi extends CasBaseApi {
    constructor(baseUrl, tenantToken, ciamToken, userUuid) {
        super(baseUrl, tenantToken, ciamToken, userUuid);
        this.tenantToken = tenantToken;
        this.ciamToken = ciamToken;
        this.userUuid = userUuid;
        this.name = "CasAddressesApi";
        this.Endpoints = this.isEvo ? EvoEndpoints : Endpoints;
    }
    static init() {
        const config = getConfig();
        const user = getUser();
        return new CasAddressesApi(config.CAS_URL, config.CAS_X_DES_AK_TENANT_TOKEN, user.ciam_token, user.uuid || "");
    }
    fromCasAddress({ id, country_id, street, telephone, postcode, city, firstname, lastname, default_billing = false, default_shipping = false, sublocality_level_2 = "", administrative_area_level_2 = "", extension_attributes = { subscription: false }, district = "", building_number = "", landmark = "", neighborhood = "", premise = "", }) {
        return {
            id: String(id),
            city,
            streetName: street.length > 0 ? street[0] : "",
            streetNumber: street.length > 1 ? street[1] : "",
            streetAdditional: street.length > 2 ? street[2] : "",
            countryCode: country_id,
            postcode,
            firstname,
            lastname,
            telephone,
            isDefaultBilling: default_billing,
            isDefaultShipping: default_shipping,
            provinceName: administrative_area_level_2,
            districtName: sublocality_level_2,
            isSubscription: extension_attributes.subscription,
            district,
            buildingNumber: building_number,
            neighborhood,
            premise,
            landmark,
        };
    }
    toCasAddress(address) {
        var _a, _b;
        return {
            country_code: address.countryCode,
            street: [address.streetName, address.streetNumber, address.streetAdditional],
            phone: address.telephone,
            post_code: address.postcode,
            city: address.city,
            first_name: address.firstname,
            last_name: address.lastname,
            default_shipping: (_a = address.isDefaultShipping) !== null && _a !== void 0 ? _a : false,
            default_billing: (_b = address.isDefaultBilling) !== null && _b !== void 0 ? _b : false,
            administrative_area_level_2: address.provinceName || "",
            sublocality_level_2: address.districtName || "",
            district: address.district || "",
            building_number: address.buildingNumber || "",
            neighborhood: address.neighborhood || "",
            landmark: address.landmark || "",
            premise: address.premise || "",
        };
    }
    prepareAddresses(addresses) {
        return addresses.map((address) => this.fromCasAddress(address));
    }
    prepareAndFindAddress(addresses, addressId) {
        const storedAddresses = addresses.map((address) => this.fromCasAddress(address));
        const updatedAddress = storedAddresses.find(({ id }) => id === addressId);
        return {
            storedAddresses,
            updatedAddress,
        };
    }
    getAddresses() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { data } = yield this.request(this.replaceUuid(this.Endpoints.ProfilePath), {}, true);
                return { addresses: this.prepareAddresses(data.consumer.addresses) };
            }
            const { addresses } = yield this.request(this.replaceUuid(this.Endpoints.ProfilePath));
            return {
                addresses: this.prepareAddresses(addresses),
            };
        });
    }
    createAddress(address) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { addresses } = yield this.request(this.replaceUuid(this.Endpoints.StoreAddressesUrl), {
                    method: "POST",
                    body: JSON.stringify(this.toCasAddress(address)),
                }, true);
                return {
                    addresses: this.prepareAddresses(addresses),
                    address: this.fromCasAddress(addresses[addresses.length - 1]),
                };
            }
            const { customer: { addresses }, } = yield this.request(this.replaceUuid(this.Endpoints.StoreAddressesUrl), {
                method: "POST",
                body: JSON.stringify(this.toCasAddress(address)),
            });
            return {
                addresses: this.prepareAddresses(addresses),
                address: this.fromCasAddress(addresses[addresses.length - 1]),
            };
        });
    }
    updateAddress(addressId, address) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.formatEndpointUrl(this.Endpoints.UpdateAddressUrl, { ADDRESS_ID: addressId, UUID: this.userUuid });
            if (this.isEvo) {
                const { addresses } = yield this.request(url, {
                    method: "PUT",
                    body: JSON.stringify(this.toCasAddress(address)),
                }, true);
                const { storedAddresses, updatedAddress } = this.prepareAndFindAddress(addresses, addressId);
                return {
                    addresses: storedAddresses,
                    address: updatedAddress,
                };
            }
            const { customer: { addresses }, } = yield this.request(url, {
                method: "PUT",
                body: JSON.stringify(this.toCasAddress(address)),
            });
            const { storedAddresses, updatedAddress } = this.prepareAndFindAddress(addresses, addressId);
            return {
                addresses: storedAddresses,
                address: updatedAddress,
            };
        });
    }
    setDefaultShippingAddress(addressId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { addresses } = yield this.request(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateDefaultShippingAddress), addressId), {
                    method: "PUT",
                    body: null,
                }, true);
                const { storedAddresses, updatedAddress } = this.prepareAndFindAddress(addresses, addressId);
                return {
                    addresses: storedAddresses,
                    address: updatedAddress,
                };
            }
            const { customer: { addresses }, } = yield this.request(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateDefaultShippingAddress), addressId), {
                method: "PUT",
                body: null,
            });
            const { storedAddresses, updatedAddress } = this.prepareAndFindAddress(addresses, addressId);
            return {
                addresses: storedAddresses,
                address: updatedAddress,
            };
        });
    }
    setDefaultBillingAddress(addressId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { addresses } = yield this.request(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateDefaultBillingAddress), addressId), {
                    method: "PUT",
                    body: null,
                }, true);
                const { storedAddresses, updatedAddress } = this.prepareAndFindAddress(addresses, addressId);
                return {
                    addresses: storedAddresses,
                    address: updatedAddress,
                };
            }
            const { customer: { addresses }, } = yield this.request(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateDefaultBillingAddress), addressId), {
                method: "PUT",
                body: null,
            });
            const { storedAddresses, updatedAddress } = this.prepareAndFindAddress(addresses, addressId);
            return {
                addresses: storedAddresses,
                address: updatedAddress,
            };
        });
    }
    deleteAddress(addressId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { statusCode } = yield this.request(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateAddressUrl), addressId), {
                    method: "DELETE",
                }, true);
                return {
                    statusCode,
                };
            }
            const { customer: { addresses }, statusCode, } = yield this.request(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateAddressUrl), addressId), {
                method: "DELETE",
            });
            const addressesList = this.prepareAddresses(addresses);
            return {
                addresses: addressesList,
                statusCode,
            };
        });
    }
}
//# sourceMappingURL=CasAddressesApi.js.map